/**
 * HUD (FR-005/006/008/013).
 * 점수·콤보 배수·남은 물건 수 표시. 리셋 버튼. 음향 크기 슬라이더(0=음소거).
 */
import type { EventBus } from '../core/EventBus';
import type { Session } from '../gameplay/Session';
import { t } from '../i18n/i18n';

export class Hud {
  private scoreEl: HTMLElement;
  private comboEl: HTMLElement;
  private fillEl: HTMLElement;
  private gaugeLabel: HTMLElement;
  private hudEl: HTMLElement;
  private gaugeEl: HTMLElement;

  constructor(
    parent: HTMLElement,
    bus: EventBus,
    private session: Session,
    /** 우상단 음향 크기 컨트롤: 0.0–1.0 설정/조회(0이면 음소거). */
    private sound: { setVolume: (v: number) => void; getVolume: () => number },
  ) {
    const wrap = document.createElement('div');
    wrap.innerHTML = `
      <div class="hud">
        <div class="stats">
          <div class="score" id="dr-score">0</div>
          <div class="combo" id="dr-combo"></div>
        </div>
        <div class="right">
          <button class="icon-btn" id="dr-reset" title="${t('ttReset')}">↻</button>
          <div class="sound-wrap">
            <button class="icon-btn" id="dr-sound" title="${t('ttSound')}">🔊</button>
            <div class="volume-popup hidden" id="dr-volume-popup">
              <input type="range" id="dr-volume" min="0" max="100" step="1" aria-label="${t('ttSound')}" />
            </div>
          </div>
        </div>
      </div>
      <div class="gauge" id="dr-gauge">
        <div class="bar"><div class="fill" id="dr-fill"></div></div>
        <div class="label" id="dr-gauge-label">${t('itemsLeft', { n: 0 })}</div>
      </div>`;
    parent.appendChild(wrap);

    this.hudEl = wrap.querySelector('.hud')!;
    this.gaugeEl = wrap.querySelector('#dr-gauge')!;
    this.scoreEl = wrap.querySelector('#dr-score')!;
    this.comboEl = wrap.querySelector('#dr-combo')!;
    this.fillEl = wrap.querySelector('#dr-fill')!;
    this.gaugeLabel = wrap.querySelector('#dr-gauge-label')!;

    wrap.querySelector<HTMLButtonElement>('#dr-reset')!.addEventListener('click', () =>
      this.session.resetInPlace(),
    );

    // 음향 크기: 스피커 버튼 → 슬라이더 팝업 토글, 슬라이더 → 크기 조절(0=음소거).
    const soundBtn = wrap.querySelector<HTMLButtonElement>('#dr-sound')!;
    const popup = wrap.querySelector<HTMLElement>('#dr-volume-popup')!;
    const slider = wrap.querySelector<HTMLInputElement>('#dr-volume')!;
    const renderIcon = (vol: number): void => {
      soundBtn.textContent = vol <= 0 ? '🔇' : vol < 0.5 ? '🔉' : '🔊';
      soundBtn.classList.toggle('muted', vol <= 0);
    };
    slider.value = String(Math.round(this.sound.getVolume() * 100));
    renderIcon(this.sound.getVolume());
    soundBtn.addEventListener('click', () => popup.classList.toggle('hidden'));
    slider.addEventListener('input', () => {
      const vol = Number(slider.value) / 100;
      this.sound.setVolume(vol);
      renderIcon(vol);
    });

    bus.on('score:changed', (e) => {
      this.scoreEl.textContent = e.score.toLocaleString();
      if (e.combo >= 2) {
        this.comboEl.textContent = t('combo', { n: e.combo, m: e.multiplier.toFixed(1) });
        this.comboEl.classList.add('pop');
        setTimeout(() => this.comboEl.classList.remove('pop'), 90);
      } else {
        this.comboEl.textContent = '';
      }
    });
    bus.on('combo:reset', () => (this.comboEl.textContent = ''));
    // "남은 물건": 책상 위 온전한 부술 물건 수(파괴할수록 감소). 바는 남은/전체 비율.
    bus.on('objects:remaining', (e) => {
      const pct = e.total > 0 ? (e.remaining / e.total) * 100 : 0;
      this.fillEl.style.width = `${pct}%`;
      this.gaugeLabel.textContent = t('itemsLeft', { n: e.remaining });
    });
    bus.on('session:reset', () => {
      this.scoreEl.textContent = '0';
      this.comboEl.textContent = '';
    });
  }

  setVisible(v: boolean): void {
    this.hudEl.classList.toggle('hidden', !v);
    this.gaugeEl.classList.toggle('hidden', !v);
  }
}
